import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { AnimatePresence, motion } from "motion/react"
import { AfishaBanner } from "../../types/entities/banner"

const BODY = "'Manrope', sans-serif"
const MONO = "'JetBrains Mono', monospace"

export function hasBannerAdInfo(banner: AfishaBanner): boolean {
    return Boolean(banner.erid?.trim() || banner.legal_entity_inn?.trim())
}

type Props = {
    banner: AfishaBanner
    className?: string
}

function InfoIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
            <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.25" />
            <path d="M7 6.25V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="7" cy="4.25" r="0.75" fill="currentColor" />
        </svg>
    )
}

export default function BannerAdDisclosure({ banner, className = "" }: Props) {
    const [open, setOpen] = useState(false)

    useEffect(() => {
        if (!open) return

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOpen(false)
        }

        document.addEventListener("keydown", onKeyDown)
        return () => document.removeEventListener("keydown", onKeyDown)
    }, [open])

    if (!hasBannerAdInfo(banner)) return null

    const erid = banner.erid?.trim()
    const legalEntity = banner.legal_entity_inn?.trim()

    return (
        <>
            <button
                type="button"
                onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()
                    setOpen(true)
                }}
                className={`inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-[#0a1028]/30 px-1.5 py-px text-[9px] font-semibold text-white/40 backdrop-blur-sm transition md:px-2.5 md:py-1 md:text-[11px] md:hover:border-white/15 md:hover:bg-[#0a1028]/40 md:hover:text-white/50 ${className}`}
                style={{ fontFamily: BODY }}
                aria-label="Сведения о рекламе">
                <span className="hidden md:inline-flex">
                    <InfoIcon />
                </span>
                Реклама
            </button>

            {createPortal(
                <AnimatePresence>
                    {open && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="fixed inset-0 z-[110] flex cursor-pointer items-end justify-center overflow-hidden px-4 pb-[max(1rem,var(--safe-bottom))] md:items-center md:pb-4"
                            style={{
                                background: "rgba(10,18,42,0.75)",
                                backdropFilter: "blur(4px)",
                                WebkitBackdropFilter: "blur(4px)",
                            }}
                            onMouseDown={e => {
                                if (e.target === e.currentTarget) setOpen(false)
                            }}>
                            <motion.div
                                initial={{ y: 24, opacity: 0, scale: 0.98 }}
                                animate={{ y: 0, opacity: 1, scale: 1 }}
                                exit={{ y: 24, opacity: 0, scale: 0.98 }}
                                transition={{ type: "spring", stiffness: 360, damping: 30 }}
                                className="w-full max-w-[360px] cursor-default overflow-hidden rounded-[20px] border border-white/12 shadow-[0_16px_48px_rgba(0,0,0,0.45)] md:rounded-[24px]"
                                style={{ background: "linear-gradient(160deg, #1e2f58 0%, #1a1438 100%)" }}
                                onMouseDown={e => e.stopPropagation()}
                                role="dialog"
                                aria-modal="true"
                                aria-labelledby="banner-ad-disclosure-title">
                                <div className="px-5 pb-4 pt-5">
                                    <div className="flex items-start gap-3">
                                        <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/[0.06] text-white/70">
                                            <InfoIcon />
                                        </div>
                                        <div className="min-w-0">
                                            <h3
                                                id="banner-ad-disclosure-title"
                                                className="text-[16px] font-bold leading-snug text-white"
                                                style={{ fontFamily: BODY }}>
                                                Сведения о рекламе
                                            </h3>
                                            <p className="mt-1 text-[12px] leading-relaxed text-white/45" style={{ fontFamily: BODY }}>
                                                Информация о рекламодателе в соответствии с требованиями маркировки рекламы
                                            </p>
                                        </div>
                                    </div>

                                    <dl className="mt-4 space-y-3">
                                        {legalEntity && (
                                            <div>
                                                <dt
                                                    className="text-[10px] uppercase tracking-[0.06em] text-white/40"
                                                    style={{ fontFamily: MONO, fontWeight: 700 }}>
                                                    Рекламодатель
                                                </dt>
                                                <dd className="mt-1 text-[14px] leading-snug text-white/85" style={{ fontFamily: BODY }}>
                                                    {legalEntity}
                                                </dd>
                                            </div>
                                        )}
                                        {erid && (
                                            <div>
                                                <dt
                                                    className="text-[10px] uppercase tracking-[0.06em] text-white/40"
                                                    style={{ fontFamily: MONO, fontWeight: 700 }}>
                                                    ERID
                                                </dt>
                                                <dd
                                                    className="mt-1 break-all text-[14px] leading-snug text-white/85"
                                                    style={{ fontFamily: MONO, fontWeight: 600 }}>
                                                    {erid}
                                                </dd>
                                            </div>
                                        )}
                                    </dl>
                                </div>

                                <button
                                    type="button"
                                    onClick={() => setOpen(false)}
                                    className="w-full border-t border-white/10 py-3.5 text-[13px] font-bold text-[#349be6] transition hover:bg-white/[0.06]"
                                    style={{ fontFamily: MONO }}>
                                    Закрыть
                                </button>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>,
                document.body,
            )}
        </>
    )
}
